import { Controller } from "@hotwired/stimulus"

// Simplified capital call form controller
// Percentage input directly maps to percentage_of_fund (% of total fund commitment)
// No more confusing "% of available" conversion
export default class extends Controller {
  static targets = ["slider", "percentageInput", "amountInput", "investorAmount", "quickButton", "percentageField"]
  static values = {
    fundSize: Number,       // Total fund size (initial_capital) in dollars
    maxPercentage: Number   // Remaining callable as % of total fund (e.g. 75 means 75% remaining)
  }

  connect() {
    // Initialize from the percentage input
    this.updateFromPercentage(parseFloat(this.percentageInputTarget.value) || 0)
  }

  // Quick-select button clicked (value is direct % of fund: 10, 25, 50, or max)
  setPercentage(event) {
    event.preventDefault()
    const percentage = Math.min(parseFloat(event.currentTarget.dataset.percentage), this.maxPercentageValue)
    this.percentageInputTarget.value = percentage
    this.sliderTarget.value = percentage
    this.updateFromPercentage(percentage)
  }

  // Slider dragged (0 to maxPercentage, representing % of fund)
  sliderChanged() {
    const percentage = parseFloat(this.sliderTarget.value)
    this.percentageInputTarget.value = parseFloat(percentage.toFixed(2))
    this.updateFromPercentage(percentage)
  }

  // Percentage text input changed (direct % of fund)
  percentageChanged() {
    let percentage = parseFloat(this.percentageInputTarget.value) || 0
    percentage = Math.max(0, Math.min(percentage, this.maxPercentageValue))
    this.sliderTarget.value = percentage
    this.updateFromPercentage(percentage)
  }

  // Dollar amount input changed - reverse calculate the percentage
  amountChanged() {
    const amount = this.parseNumber(this.amountInputTarget.value)
    let percentage = this.fundSizeValue > 0
      ? (amount / this.fundSizeValue) * 100
      : 0
    percentage = Math.min(percentage, this.maxPercentageValue)

    this.sliderTarget.value = percentage
    this.percentageInputTarget.value = parseFloat(percentage.toFixed(2))
    this.percentageFieldTarget.value = percentage
    this.updateInvestorAmounts(percentage)
    this.updateQuickButtons(percentage)
  }

  // Format amount on blur
  formatAmount() {
    const amount = this.parseNumber(this.amountInputTarget.value)
    this.amountInputTarget.value = this.formatNumber(amount)
  }

  // --- Private helpers ---

  // Main update method - takes % of fund and syncs everything
  updateFromPercentage(percentage) {
    const amount = this.fundSizeValue * (percentage / 100)

    this.amountInputTarget.value = this.formatNumber(amount)
    this.percentageFieldTarget.value = percentage
    this.updateInvestorAmounts(percentage)
    this.updateQuickButtons(percentage)
  }

  // Update each investor's call amount based on their commitment * percentage
  updateInvestorAmounts(percentage) {
    this.investorAmountTargets.forEach((el) => {
      const commitment = parseFloat(el.dataset.commitment)
      const investorAmount = commitment * (percentage / 100)
      el.textContent = "$" + this.formatNumber(investorAmount)
    })
  }

  // Highlight the active quick-select button
  updateQuickButtons(percentage) {
    this.quickButtonTargets.forEach((btn) => {
      const btnPercentage = parseFloat(btn.dataset.percentage)
      const isActive = Math.abs(btnPercentage - percentage) < 0.01

      if (isActive) {
        btn.classList.remove("bg-white", "dark:bg-gray-800", "text-gray-900", "dark:text-white", "hover:bg-gray-100", "dark:hover:bg-gray-700")
        btn.classList.add("bg-primary-700", "dark:bg-primary-600", "text-white", "dark:text-white")
      } else {
        btn.classList.remove("bg-primary-700", "dark:bg-primary-600", "text-white")
        btn.classList.add("bg-white", "dark:bg-gray-800", "text-gray-900", "dark:text-white", "hover:bg-gray-100", "dark:hover:bg-gray-700")
      }
    })
  }

  formatNumber(num) {
    return Math.round(num).toLocaleString()
  }

  parseNumber(str) {
    return parseFloat(str.replace(/,/g, "")) || 0
  }
}
